import styled from 'styled-components';
import COLORS from '../../utils/Colors';
import { NAVBAR_HEIGHT } from '../../utils/constants';

export const PageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding-top: ${NAVBAR_HEIGHT};
    min-height: 100vh;
    background-color: ${COLORS.freshaGray50};
`;

export const Container = styled.div`
    display: flex;
    width: 60vw;
    flex-direction: column;
    padding: 2rem 0;

    @media (max-width: ${({ theme }) => theme.screenSizes.laptopL}) {
        width: 75vw;
    };

    @media (max-width: ${({ theme }) => theme.screenSizes.laptop}) {
        width: 85vw;
    };

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        width: 95vw;
        padding: 1rem;
    };
`;

export const ProfileHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 1.5rem;
    padding: 2rem;
    background: linear-gradient(135deg, ${COLORS.mainTeal} 0%, ${COLORS.mainTealDark} 100%);
    border-radius: 16px;
    margin-bottom: 2rem;
    color: ${COLORS.white};

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        flex-direction: column;
        text-align: center;
        padding: 1.5rem;
    };
`;

export const Avatar = styled.div`
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: ${COLORS.white};
    color: ${COLORS.mainTeal};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 2rem;
    font-weight: 600;
    font-family: 'Raleway', sans-serif;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        width: 64px;
        height: 64px;
        font-size: 1.5rem;
    }
`;

export const UserInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
`;

export const UserEmail = styled.h2`
    font-family: 'Raleway', sans-serif;
    font-size: 1.5rem;
    font-weight: 600;
    margin: 0;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        font-size: 1.125rem;
        word-break: break-all;
    }
`;

export const MemberSince = styled.p`
    font-size: 0.875rem;
    opacity: 0.9;
    margin: 0;
`;

export const Section = styled.section`
    margin-bottom: 2rem;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        margin-bottom: 1.5rem;
    }
`;

export const SectionTitle = styled.h3`
    font-family: 'Raleway', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
    margin: 0 0 1rem 0;
    padding-bottom: 0.5rem;
    border-bottom: 2px solid ${COLORS.freshaGray200};

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        font-size: 1.125rem;
    }
`;

export const BookingCard = styled.div`
    background: ${COLORS.white};
    border-radius: 12px;
    padding: 1.25rem;
    margin-bottom: 1rem;
    box-shadow: 0 1px 3px ${COLORS.rgba000000000014};
    border: 1px solid ${COLORS.freshaGray200};
    transition: box-shadow 0.2s ease;

    &:hover {
        box-shadow: 0 4px 12px ${COLORS.rgba000000000014};
    }

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        padding: 1rem;
    }
`;

export const BookingHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 0.75rem;

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        flex-direction: column;
        gap: 0.5rem;
    };
`;

export const BusinessName = styled.h4`
    font-family: 'Raleway', sans-serif;
    font-size: 1.125rem;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
    margin: 0;
`;

interface StatusBadgeProps {
    $status: string;
}

const getStatusColor = (status: string) => {
    switch (status) {
        case 'PENDING':
            return { bg: '#fef3c7', text: '#92400e' };
        case 'CONFIRMED':
            return { bg: '#d1fae5', text: '#065f46' };
        case 'COMPLETED':
            return { bg: '#f3f4f6', text: '#374151' };
        case 'CANCELLED':
            return { bg: '#fee2e2', text: '#991b1b' };
        case 'NO SHOW':
            return { bg: '#fecaca', text: '#7f1d1d' };
        case 'PENDING_PAYMENT':
            return { bg: '#fef3c7', text: '#92400e' };
        default:
            return { bg: '#f3f4f6', text: '#374151' };
    }
};

export const StatusBadge = styled.span<StatusBadgeProps>`
    padding: 0.25rem 0.75rem;
    border-radius: 9999px;
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    background-color: ${({ $status }) => getStatusColor($status).bg};
    color: ${({ $status }) => getStatusColor($status).text};
`;

export const BookingDetails = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    color: ${COLORS.freshaGray600};
    font-size: 0.875rem;
`;

export const BookingDateTime = styled.p`
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

export const BookingServices = styled.p`
    margin: 0;
    color: ${COLORS.freshaGray500};
`;

export const BookingActions = styled.div`
    display: flex;
    gap: 0.75rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid ${COLORS.freshaGray100};

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        flex-direction: column;
    }
`;

export const ActionButton = styled.button<{ $variant?: 'primary' | 'secondary' | 'danger' }>`
    padding: 0.5rem 1rem;
    border-radius: 8px;
    font-size: 0.875rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;

    ${({ $variant }) => {
        switch ($variant) {
            case 'primary':
                return `
                    background-color: ${COLORS.mainTeal};
                    color: ${COLORS.white};
                    border: none;
                    &:hover {
                        background-color: ${COLORS.mainTealDark};
                    }
                `;
            case 'danger':
                return `
                    background-color: transparent;
                    color: #dc2626;
                    border: 1px solid #dc2626;
                    &:hover {
                        background-color: #fee2e2;
                    }
                `;
            default:
                return `
                    background-color: transparent;
                    color: ${COLORS.freshaGray700};
                    border: 1px solid ${COLORS.freshaGray200};
                    &:hover {
                        background-color: ${COLORS.freshaGray100};
                    }
                `;
        }
    }}

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        width: 100%;
        justify-content: center;
    }
`;

export const EmptyState = styled.div`
    text-align: center;
    padding: 2rem;
    color: ${COLORS.freshaGray500};
    background: ${COLORS.white};
    border-radius: 12px;
    border: 1px dashed ${COLORS.freshaGray200};
`;

export const EmptyStateText = styled.p`
    margin: 0;
    font-size: 0.875rem;
`;

export const SettingsSection = styled.div`
    background: ${COLORS.white};
    border-radius: 12px;
    padding: 1.5rem;
    border: 1px solid ${COLORS.freshaGray200};

    @media (max-width: ${({ theme }) => theme.screenSizes.tablet}) {
        padding: 1rem;
    }
`;

export const SettingsItem = styled.button`
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.75rem;
    background: transparent;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 0.875rem;
    color: ${COLORS.freshaGray700};
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${COLORS.freshaGray50};
    }

    &:not(:last-child) {
        border-bottom: 1px solid ${COLORS.freshaGray100};
    }
`;

export const SettingsItemDanger = styled(SettingsItem)`
    color: #dc2626;

    &:hover {
        background-color: #fee2e2;
    }
`;

export const LoadingContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    min-height: 200px;
    color: ${COLORS.freshaGray500};
`;

export const Separator = styled.hr`
    border: none;
    border-top: 1px solid ${COLORS.freshaGray200};
    margin: 0.5rem 0;
`;

// Modal styles
export const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    padding: 16px;
`;

export const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    max-width: 450px;
    width: 100%;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

export const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid ${COLORS.freshaGray200};
`;

export const ModalTitle = styled.h3`
    margin: 0;
    font-size: 18px;
    font-weight: 600;
    color: ${COLORS.freshaGray900};
`;

export const ModalCloseButton = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: ${COLORS.freshaGray500};
    
    &:hover {
        color: ${COLORS.freshaGray700};
    }
`;

export const ModalBody = styled.div`
    padding: 24px;
`;

export const ModalText = styled.p`
    margin: 0 0 16px 0;
    font-size: 15px;
    color: ${COLORS.freshaGray700};
`;

export const ModalBookingInfo = styled.div`
    background: ${COLORS.freshaGray100};
    border-radius: 12px;
    padding: 16px;
`;

export const ModalBookingDetail = styled.div`
    font-size: 14px;
    color: ${COLORS.freshaGray700};
    margin-bottom: 8px;
    
    &:last-child {
        margin-bottom: 0;
    }
    
    strong {
        color: ${COLORS.freshaGray900};
    }
`;

export const ModalFooter = styled.div`
    display: flex;
    gap: 12px;
    padding: 16px 24px 24px;
`;

export const ModalButton = styled.button<{ $variant: 'primary' | 'secondary' | 'danger' }>`
    flex: 1;
    padding: 12px 20px;
    border-radius: 8px;
    font-size: 15px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s ease;
    
    ${({ $variant }) => {
        switch ($variant) {
            case 'danger':
                return `
                    background-color: #ef4444;
                    color: white;
                    border: none;
                    &:hover:not(:disabled) {
                        background-color: #dc2626;
                    }
                    &:disabled {
                        opacity: 0.6;
                        cursor: not-allowed;
                    }
                `;
            case 'secondary':
                return `
                    background-color: white;
                    color: ${COLORS.freshaGray700};
                    border: 1px solid ${COLORS.freshaGray200};
                    &:hover {
                        background-color: ${COLORS.freshaGray100};
                    }
                `;
            default:
                return `
                    background-color: ${COLORS.hsl177063032};
                    color: white;
                    border: none;
                    &:hover {
                        opacity: 0.9;
                    }
                `;
        }
    }}
`;
